/**
 * ADR 0005: every browser-only API goes through here, with web no-ops, so the Capacitor build
 * can swap in a native implementation without a change anywhere else. `window.open`, `alert`
 * and `document.cookie` are banned outright (the ESLint rules in the root config enforce it);
 * the credit sheet's links are the single permitted way out of the student experience
 * (spec 9.4) and go through `ExternalLink`.
 */

import type { ReactNode } from "react";
import { createElement } from "react";

export function ExternalLink({
  href,
  children,
  className,
}: {
  href: string;
  children: ReactNode;
  className?: string;
}) {
  return createElement(
    "a",
    { href, target: "_blank", rel: "noopener noreferrer", className },
    children,
  );
}

/** No-op on web; a Capacitor build maps this to `@capacitor/haptics`. */
export function vibrate(_pattern?: number): void {
  // Intentionally empty: no vibration API use on the web build.
}

/** No-op on web; a Capacitor build maps this to `@capacitor/status-bar`. */
export function setStatusBarDark(): void {
  // Intentionally empty: the web build's theme-color meta tag already covers this.
}

/**
 * The print dialog, for the teacher area's login strips (spec 10.1). Behind the adapter like
 * every other browser-only API: the student area must never reach it, which is why the ESLint
 * rule bans `window.print` everywhere except this file, and a Capacitor build would map it to
 * a share sheet instead. Teachers print from a desktop browser, so the web path is the real one.
 */
export function printPage(): void {
  // eslint-disable-next-line no-restricted-properties -- the one permitted call site; see above.
  window.print();
}

/**
 * Hands the viewer a file the app already holds in memory — the class CSV (spec 10.1). An
 * object URL and a synthetic anchor click, never `window.open`, which ADR 0005 bans outright
 * and which a WebView would swallow anyway. The URL is revoked on the next tick, once the
 * browser has taken the blob.
 */
export function saveFile(fileName: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** `navigator.onLine` behind the adapter so the offline state has one source (build spec 4.4). */
export function isOnline(): boolean {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

export function onConnectivityChange(handler: () => void): () => void {
  window.addEventListener("online", handler);
  window.addEventListener("offline", handler);
  return () => {
    window.removeEventListener("online", handler);
    window.removeEventListener("offline", handler);
  };
}
